import React, { useState, useCallback, useMemo } from 'react';
import { 
  FormControl, 
  Select, 
  MenuItem, 
  TextField, 
  IconButton,
  Chip,
  Box,
  Divider
} from '@mui/material';
import { 
  Search as SearchIcon,
  Clear as ClearIcon,
  ViewList as FlatViewIcon,
  AccountTree as TreeViewIcon,
  ViewModule as HybridViewIcon,
  GridView as GridViewIcon,
  ViewComfy as MasonryViewIcon,
  ArrowUpward as AscIcon,
  ArrowDownward as DescIcon
} from '@mui/icons-material';

import FlatView from './FlatView';
import HybridView from './HybridView';
import SearchableGrid from './SearchableGrid';
import MasonryView from './MasonryView';
import { StackItem } from '../core/StackItem';
import { StackItemGrid } from '../core/StackItemGrid';

/**
 * ViewSwitcher - Universal view switcher for StackContainer
 * 
 * This component provides a unified interface for switching between:
 * - Tree view (hierarchical StackItem)
 * - Flat view (search results with pagination)
 * - Hybrid view (categories + flat items)
 * - Grid view (StackItemGrid)
 * 
 * Features:
 * - Search input with real-time search
 * - View mode selector
 * - Facet display and filtering
 * - Domain (di) support
 * - Template configuration per view
 * - Debug mode support
 * 
 * Props:
 * - rootId: Root ID for hierarchy/search scope
 * - di: Domain ID for filtering
 * - defaultView: Default view mode ('tree', 'flat', 'hybrid', 'grid')
 * - onSelect: Selection callback
 * - selectedItems: Currently selected items
 * - showSearch: Show search input (default: true)
 * - showViewSelector: Show view mode selector (default: true)
 * - showFuzzyToggle: Show fuzzy search toggle (default: true)
 * - searchPlaceholder: Search input placeholder
 * - searchFields: Fields to search in
 * - facetFields: Fields to generate facets for
 * - itemTemplate: Template for list items
 * - categoryTemplate: Template for categories (hybrid view)
 * - useCompact: Use compact rendering
 * - debug: Enable debug logging
 * - showDeleteButton: Show delete button (cross) on items (default: true)
 * 
 * Tree/Grid view props:
 * - registerItem: Item registration callback
 * - handleMoveItem: Move item handler
 * - refreshAfterDrop: Refresh handler after drop
 * - rows: Number of rows for grid
 * - columnsPerRow: Columns per row for grid
 * - imageWidth: Image width for grid
 * - ratio: Aspect ratio for grid
 * - imageClassName: CSS class for grid images
 * - textClassName: CSS class for grid text
 * - animateClass: Animation CSS class
 */

const VIEW_MODES = [
  { 
    id: 'tree', 
    label: 'Tree View', 
    icon: <TreeViewIcon />,
    description: 'Hierarchical structure' 
  },
  { 
    id: 'flat', 
    label: 'Flat View', 
    icon: <FlatViewIcon />,
    description: 'Search results with pagination' 
  },
  { 
    id: 'hybrid', 
    label: 'Hybrid View', 
    icon: <HybridViewIcon />,
    description: 'Categories + flat items' 
  },
  { 
    id: 'grid', 
    label: 'Grid View', 
    icon: <GridViewIcon />,
    description: 'Searchable grid layout' 
  },
  { 
    id: 'masonry', 
    label: 'Masonry View', 
    icon: <MasonryViewIcon />,
    description: 'Pinterest-style staggered grid' 
  },
  { 
    id: 'stackgrid', 
    label: 'Hierarchy Grid', 
    icon: <GridViewIcon />,
    description: 'Hierarchical grid layout' 
  }
];

const ViewSwitcher = ({
  // Core props
  rootId,
  di = null,
  defaultView = 'tree',
  onSelect,
  selectedItems = [],
  
  // UI props
  showSearch = true,
  showViewSelector = true,
  showFuzzyToggle = true,
  showSearchInfo = true,
  showLoadingState = true,
  showFacets = true,
  showFacetCounts = true,
  showSortControls = true,
  searchPlaceholder = 'Search...',
  
  // Search props
  searchFields = [],
  facetFields = [],
  facetConfig = [], // Array of {field, label} objects for custom facet labels
  sortBy: initialSortBy = 'title',
  sortOrder: initialSortOrder = 1, // 1 for asc, -1 for desc
  
  // Template props
  itemTemplate = null,
  categoryTemplate = null,
  useCompact = true,
  templateProjection = [], // Fields to fetch for template variable replacement
  
  // Styling props
  itemGap = 0, // Gap between items in flat view (in pixels)
  itemHeight = 120, // Height of each item in pixels
  fixedHeight = false, // Force fixed height instead of dynamic calculation
  
  // Additional buttons
  additionalButtons = null, // Additional buttons to render in search controls
  showSeparator = false, // Show separator line between items
  separatorWidth = 1, // Separator line width in pixels
  separatorColor = "#e0e0e0", // Separator line color
  
  // Tree/Grid view props
  registerItem,
  handleMoveItem,
  refreshAfterDrop,
  rows = 100,
  columnsPerRow = 3,
  imageWidth = '64px',
  ratio = '1/1',
  imageClassName = '',
  textClassName = '',
  animateClass = '',
  
  // Debug
  debug = false,
  
  // Additional context
  extraContext = null,
  
  // Delete button visibility
  showDeleteButton = false
}) => {
  // State management
  const [currentView, setCurrentView] = useState(defaultView);
  const [searchQuery, setSearchQuery] = useState('');
  const [fuzzySearch, setFuzzySearch] = useState(false);
  const [activeFacets, setActiveFacets] = useState(new Map());
  const [sortBy, setSortBy] = useState(initialSortBy);
  const [sortOrder, setSortOrder] = useState(initialSortOrder);

  // Handle search input changes - use useCallback to prevent unnecessary re-renders
  const handleSearchChange = useCallback((event) => {
    setSearchQuery(event.target.value);
  }, []);

  // Clear search - use useCallback to prevent unnecessary re-renders
  const clearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  // Handle view mode change - use useCallback to prevent unnecessary re-renders
  const handleViewChange = useCallback((event) => {
    const newView = event.target.value;
    setCurrentView(newView);
    
    if (debug) {
      console.log('[ViewSwitcher] View changed to:', newView);
    }
  }, [debug]);

  // Toggle fuzzy search
  const toggleFuzzySearch = () => {
    setFuzzySearch(prev => !prev);
  };

  // Handle sort field change
  const handleSortFieldChange = (event) => {
    setSortBy(event.target.value);
  };

  // Toggle sort order
  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 1 ? -1 : 1);
  };

  // Handle facet selection
  const handleFacetToggle = (field, value) => {
    setActiveFacets(prev => {
      const newFacets = new Map(prev);
      const key = `${field}:${value}`;
      
      if (newFacets.has(key)) {
        newFacets.delete(key);
      } else {
        newFacets.set(key, { field, value });
      }
      
      return newFacets;
    });
  };

  // Clear all facets
  const clearAllFacets = () => {
    setActiveFacets(new Map());
  };

  // Get display label for a field (from facetConfig or fallback to field name) - use useCallback
  const getFacetLabel = useCallback((fieldName) => {
    const config = facetConfig.find(f => f.field === fieldName);
    return config ? config.label : fieldName;
  }, [facetConfig]);

  // Get search query for API calls - memoize to prevent unnecessary re-calculations
  const getSearchQuery = useMemo(() => {
    let query = searchQuery.trim();
    
    // Add facet filters to query (simple implementation)
    activeFacets.forEach(({ field, value }) => {
      query += ` ${field}:${value}`;
    });
    
    return query;
  }, [searchQuery, activeFacets]);

  // Render search interface
  const renderSearchInterface = () => {
    if (!showSearch && !showViewSelector) return null;
    
    return (
      <Box sx={{
        padding: 2,
        backgroundColor: '#f8f9fa',
        borderBottom: '1px solid #eee',
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        {/* Search input */}
        {showSearch && (
          <TextField
            size="small"
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: '#666' }} />,
              endAdornment: searchQuery && (
                <IconButton size="small" onClick={clearSearch}>
                  <ClearIcon />
                </IconButton>
              )
            }}
            sx={{ minWidth: 200, flex: 1 }}
          />
        )}
        
        {/* Fuzzy search toggle */}
        {showSearch && showFuzzyToggle && (
          <Chip
            label="Fuzzy"
            size="small"
            variant={fuzzySearch ? 'filled' : 'outlined'}
            onClick={toggleFuzzySearch}
            sx={{ fontSize: '11px' }}
          />
        )}
        
        {/* View selector */}
        {showViewSelector && (
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select
              value={currentView}
              onChange={handleViewChange}
              displayEmpty
            >
              {VIEW_MODES.map(mode => (
                <MenuItem key={mode.id} value={mode.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {mode.icon}
                    <span>{mode.label}</span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        
        {/* Sorting controls for search views */}
        {showSortControls && (currentView === 'flat' || currentView === 'hybrid' || currentView === 'grid' || currentView === 'masonry') && (
          <>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select
                  value={sortBy}
                  onChange={handleSortFieldChange}
                  displayEmpty
                  sx={{ fontSize: '12px' }}
                >
                  <MenuItem value="title">Title</MenuItem>
                  <MenuItem value="name">Name</MenuItem>
                  <MenuItem value="firstname">First Name</MenuItem>
                  <MenuItem value="date">Date</MenuItem>
                  <MenuItem value="created">Created</MenuItem>
                  <MenuItem value="updated">Updated</MenuItem>
                  <MenuItem value="_id">ID</MenuItem>
                </Select>
              </FormControl>
              <IconButton 
                size="small" 
                onClick={toggleSortOrder}
                title={sortOrder === 1 ? 'Sort Ascending' : 'Sort Descending'}
                sx={{ 
                  color: sortOrder === 1 ? '#1976d2' : '#d32f2f',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
                }}
              >
                {sortOrder === 1 ? <AscIcon /> : <DescIcon />}
              </IconButton>
            </Box>
          </>
        )}
        
        {/* Active facets */}
        {activeFacets.size > 0 && (
          <>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', color: '#666' }}>Filters:</span>
              {Array.from(activeFacets.values()).map(({ field, value }) => (
                <Chip
                  key={`${field}:${value}`}
                  label={`${getFacetLabel(field)}: ${value}`}
                  size="small"
                  onDelete={() => handleFacetToggle(field, value)}
                  sx={{ fontSize: '10px' }}
                />
              ))}
              <IconButton size="small" onClick={clearAllFacets} title="Clear all filters">
                <ClearIcon />
              </IconButton>
            </Box>
          </>
        )}
        
        {/* Additional buttons */}
        {additionalButtons && additionalButtons}
      </Box>
    );
  };

  // Render current view
  const renderCurrentView = () => {
    const commonProps = {
      rootId,
      di,
      onSelect,
      selectedItems,
      useCompact,
      debug
    };
    
    switch (currentView) {
      case 'flat':
        return (
          <FlatView
            {...commonProps}
            query={getSearchQuery}
            fields={searchFields}
            fuzzy={fuzzySearch}
            facets={facetFields}
            facetConfig={facetConfig}
            itemTemplate={itemTemplate}
            templateProjection={templateProjection}
            pageSize={50}
            sortBy={sortBy}
            sortOrder={sortOrder}
            itemGap={itemGap}
            itemHeight={itemHeight}
            fixedHeight={fixedHeight}
            showSeparator={showSeparator}
            separatorWidth={separatorWidth}
            separatorColor={separatorColor}
            showSearchInfo={showSearchInfo}
            showLoadingState={showLoadingState}
            showFacets={showFacets}
            showFacetCounts={showFacetCounts}
            extraContext={extraContext}
            showDeleteButton={showDeleteButton}
          />
        );
        
      case 'hybrid':
        return (
          <HybridView
            {...commonProps}
            query={getSearchQuery}
            fields={searchFields}
            fuzzy={fuzzySearch}
            facets={facetFields}
            facetConfig={facetConfig}
            categoryTemplate={categoryTemplate}
            itemTemplate={itemTemplate}
            showItemCount={true}
            collapsible={true}
            sortBy={sortBy}
            sortOrder={sortOrder}
            itemHeight={itemHeight}
            showSearchInfo={showSearchInfo}
            showLoadingState={showLoadingState}
            showFacets={showFacets}
            showFacetCounts={showFacetCounts}
          />
        );
        
      case 'grid':
        return (
          <SearchableGrid
            {...commonProps}
            query={getSearchQuery}
            fields={searchFields}
            fuzzy={fuzzySearch}
            facets={facetFields}
            facetConfig={facetConfig}
            itemTemplate={itemTemplate}
            pageSize={50}
            sortBy={sortBy}
            sortOrder={sortOrder}
            columnsPerRow={columnsPerRow}
            itemWidth={200}
            itemHeight={itemHeight}
            gap={16}
            showSearchInfo={showSearchInfo}
            showLoadingState={showLoadingState}
            showFacets={showFacets}
            showFacetCounts={showFacetCounts}
          />
        );
        
      case 'masonry':
        return (
          <MasonryView
            {...commonProps}
            query={getSearchQuery}
            fields={searchFields}
            fuzzy={fuzzySearch}
            facets={facetFields}
            facetConfig={facetConfig}
            itemTemplate={itemTemplate}
            pageSize={50}
            sortBy={sortBy}
            sortOrder={sortOrder}
            columnsPerRow={columnsPerRow}
            gap={16}
            showSearchInfo={showSearchInfo}
            showLoadingState={showLoadingState}
            showFacets={showFacets}
            showFacetCounts={showFacetCounts}
          />
        );
        
      case 'stackgrid':
        return (
          <StackItemGrid
            itemId={rootId}
            isRoot={true}
            onSelect={(itemId, isShift, isCtrl) => onSelect && onSelect(itemId, isShift, isCtrl)}
            selectedItems={selectedItems}
            registerItem={registerItem}
            onDrop={refreshAfterDrop}
            handleMoveItem={handleMoveItem}
            rows={rows}
            disableCurrentCardUpdate={false}
            columnsPerRow={columnsPerRow}
            imageWidth={imageWidth}
            ratio={ratio}
            imageClassName={imageClassName}
            textClassName={textClassName}
            animateClass={animateClass}
          />
        );
        
      case 'tree':
      default:
        return (
          <StackItem
            itemId={rootId}
            isRoot={true}
            onSelect={(itemId, isShift, isCtrl) => onSelect && onSelect(itemId, isShift, isCtrl)}
            selectedItems={selectedItems}
            registerItem={registerItem}
            handleMoveItem={handleMoveItem}
            rows={rows}
            disableCurrentCardUpdate={false}
          />
        );
    }
  };

  // Debug info
  const renderDebugInfo = () => {
    if (!debug) return null;
    
    return (
      <Box sx={{
        position: 'absolute',
        top: 8,
        right: 8,
        backgroundColor: 'rgba(0,0,0,0.8)',
        color: 'white',
        padding: 1,
        borderRadius: 1,
        fontSize: '10px',
        zIndex: 1000,
        maxWidth: 250
      }}>
        <div>View: {currentView}</div>
        <div>Query: "{getSearchQuery()}"</div>
        <div>Root: {rootId}</div>
        {di && <div>Domain: {di}</div>}
        <div>Fuzzy: {fuzzySearch ? 'on' : 'off'}</div>
        <div>Sort: {sortBy} ({sortOrder === 1 ? 'asc' : 'desc'})</div>
        <div>Facets: {activeFacets.size}</div>
      </Box>
    );
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {renderDebugInfo()}
      {renderSearchInterface()}
      
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {renderCurrentView()}
      </div>
    </div>
  );
};

export default ViewSwitcher;